<template>
  <div class="add-deduction-modal-container">
    <div
      class="reveal add-deduction-modal"
      ref="addDeductionModal"
      id="addDeductionModal"
      data-reveal
    >
      <div class="tender-confirm clearfix">
        <div class="add-deduction-amount-row clearfix">
          <div class="add-deduction-po-amount">
            <label class="emphasis">{{ amountLabel }}</label>
            <pre><p>{{ purchase.amount | formatCurrency({currencyCode}) }}</p></pre>
          </div>
          <div class="add-deduction-revised-amount">
            <label class="emphasis">{{ revisedLabel }}</label>
            <pre><p>{{ (purchase.amount - amount) | formatCurrency({force:true, currencyCode}) }}</p></pre>
          </div>
          <div class="add-deduction-edit-amount">
            <label class="emphasis">{{ $t('project.deduction.label') }}</label>
            <pre v-if="isContractor || !editable"><p>{{ amount | formatCurrency({currencyCode}) }}</p></pre>
            <div v-else>
              <span>{{ currencyCode || $globalProps.getCurrency() }}</span><input
                type="text"
                id="project-deduction-amount"
                v-fusioninputcheck="{
                  type: 'decimal',
                  classes: 'tooltip-class',
                  pattern: (val) => (Number(val) || 0) >= 0,
                }"
                v-model="amount"
                novalidate
              >
            </div>
          </div>
        </div>

        <div>
          <label class="emphasis">{{ $t('comment') }}</label>
          <div
            v-if="isContractor || !editable"
            class="deduction-comment"
          >
            {{ reason }}
          </div>
          <textarea
            v-else
            type="text"
            id="project-deduction-reason"
            v-model="reason"
            :placeholder="$t('typeComments')"
            v-autosize
            v-checkVisibility="{refresh: true}"
            class="autosize"
            :class="{ 'error-highlight': (reason || '').trim().length === 0 && validateForm }"
            autofocus
          />

          <label class="emphasis">{{ $t('attachments') }}</label>
          <EditAttachments
            :sourceId="deductionSrcId"
            :canEdit="editable"
            :canDelete="editable"
            :canDownloadAll="false"
            :action="getDeleteDocAction()"
          />
          <HkldDropzone
            :sourceId="deductionSrcId"
            v-show="editable"
            :action="getCreateDocAction()"
          />
        </div>
        <div
          class="add-deduction-button-bar"
          v-if="isContractor || !editable"
        >
          <button
            id="confirm_no"
            type="button"
            class="btn btn--orange"
            data-close
          >{{ $t('action.ok') }}</button>
        </div>
        <div
          class="add-deduction-button-bar"
          v-else
        >
          <button
            id="confirm_no"
            type="button"
            class="btn btn--darkblue"
            @click="cancel()"
          >{{ $t('action.cancel') }}</button>
          <button
            id="confirm_yes"
            type="button"
            class="btn btn--orange"
            @click="confirmDeduction()"
          >{{ $t('action.confirm') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash'

import HkldDropzone from '@/controls/HkldDropzone'
import EditAttachments from '@/controls/EditAttachments'

import amountUtil from '@/utils/amountUtil'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { updateProject } from '@/api/projects/request'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { ActionTypes } from '@/utils/consts'

export default {
  name: 'AddDeductionModal',
  components: {
    HkldDropzone,
    EditAttachments,
  },
  props: {
    className: {
      type: String,
      default: 'clearfix',
    },
    editable: {
      type: Boolean,
    },
  },

  data () {
    return {
      validateForm: false,
      amount: '',
      reason: '',
    }
  },

  methods: {
    ...mapActions('projects', [
      'loadProject',
    ]),
    ...mapMutations('projects', {
      updateProjectField: mutationTypes.UPDATE_PATH_FIELD,
    }),
    getCreateDocAction () {
      return ActionTypes.projects.deduction.doc.create
    },
    getDeleteDocAction () {
      return ActionTypes.projects.deduction.doc.delete
    },
    updateField (fieldName, value) {
      const path = ['deduction', fieldName]
      this.updateProjectField({ path, newValue: value })
    },
    async initModal () {
      if (!this.addDeductionModal) {
        this.addDeductionModal = await new Foundation.Reveal($('#addDeductionModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
    },
    async openModal () {
      await this.initModal()
      this.amount = this.deduction.amount
      this.reason = this.deduction.reason
      this.addDeductionModal.open()
      /* Fot textarea auto size */
      $('#project-deduction-reason').click()
    },
    closeModal () {
      this.addDeductionModal.close()
    },
    cancel () {
      this.clearErrorClasses()
      this.closeModal()
    },
    validate () {
      this.validateForm = true
      fusionInputCheckUtil.validateAllFields(undefined, '.add-deduction-modal')
      return (this.reason || '').trim().length > 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.add-deduction-modal')
    },
    clearErrorClasses () {
      this.validateForm = false
      fusionInputCheckUtil.resetAllFields()
    },
    async confirmDeduction () {
      this.reason = (this.reason || '').trim()
      if (this.amount === this.deduction.amount && this.reason === this.deduction.reason) {
        this.closeModal()
        return
      }
      if (!this.deduction || !this.validate()) {
        return
      }
      if (Number(this.amount) > Number(this.remainingBalance)) {
        notification.fireNotification(false, this.$i18n.t('project.deduction.exceeded'))
        return
      }
      const deductionData = { deduction: {amount: this.amount, reason: this.reason} }
      await updateProject(this.projectId, deductionData, ActionTypes.projects.deduction.update)
      await this.loadProject(this.projectId)
      this.closeModal()
    },
  },

  computed: {
    ...mapGetters('projects', ['currentProject', 'purchase', 'typeLabel']),
    ...mapGetters('user', ['isContractor']),
    deduction () {
      return (this.currentProject || {}).deduction || {}
    },
    deductionSrcId () {
      return (this.currentProject || {}).deductionSrcId || ''
    },
    numberType () {
      return get(this.currentProject, 'numberType')
    },
    remainingBalance () {
      const completedAmount = amountUtil.format(deep(this.currentProject, 'completedAmount') || 0)
      const poAmount = amountUtil.format(deep(this.purchase, 'amount') || 0)
      return amountUtil.format(poAmount - (completedAmount * 100) / (100 - (deep(this.currentProject, 'retention.percentValue') || 0)))
    },
    currencyCode () {
      return get(this.purchase, 'currencyCode')
    },
    projectId () {
      return this.$router.currentRoute.params.id
    },
    amountLabel () {
      return this.$t(`project.purchase.amount`, {type: this.typeLabel})
    },
    revisedLabel () {
      return `${this.$t('revised')} ${this.amountLabel}`
    },
  },

  destroyed () {
    if (this.addDeductionModal) {
      this.addDeductionModal.destroy()
      delete this.addDeductionModal
      $('#addDeductionModal').remove()
    }
  },
}
</script>

<style scoped lang="scss">
@import '~styles/tender/confirmCreate';
@import '~styles/project/style';

.add-deduction-modal-container {
  display: inline-block;
}

#project-deduction-amount {
  display: inline-block;
  width: 150px;
}

.add-deduction-amount-row {
  border-bottom: 1px solid #e0e0e0;
  padding: 15px 0;
  label {
    margin-top: 0;
  }

  label, pre>p {
    text-align: right;
  }

  pre>p {
    margin: 10px 0;
  }
}
.add-deduction-link {
  display: block;
  margin-bottom: 10px;
}

#project-deduction-reason {
  width: 100%;
}

.deduction-comment {
  text-align: left;
  word-wrap: break-word;
}
.add-deduction-button-bar {
  padding: 15px 0;
  text-align: right;
  .btn {
    margin-left: 8px;
    margin-right: 8px;
  }

  a:last-child {
    margin-left: 1vw;
  }
}

.add-deduction-po-amount {
  float: left;
}

.add-deduction-edit-amount {
  margin-right: 100px;
  float: right;
}

.add-deduction-revised-amount {
  float: right;
  max-width: 200px;
}
</style>

<style lang="scss">
.error-highlight {
  background-color: #f9ecea!important;
}

.add-deduction-modal {
  min-width: 800px;
  padding:40px 80px;
  background-color: white;
}
</style>
